/**
 * Reset Password Form Component
 * 
 * Form for completing password reset process:
 * - Verification code input
 * - New password with strength validation
 * - Password confirmation matching
 * - Error handling and user feedback
 */

import * as React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Alert, Button, Grid, TextField, Typography } from "@mui/material";

interface ResetPasswordFormProps {
  resetPassword: (verificationCode: string, newPassword: string) => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  resetPassword,
}) => {
  const [submissionError, setSubmissionError] = React.useState<string>("");

  // Form configuration with comprehensive validation
  const formik = useFormik({
    initialValues: {
      verificationCode: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      verificationCode: Yup.string().required("Verification code is required"),
      newPassword: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/\d/, "Password must contain at least one number")
        .required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match")
        .required("Confirm Password is required"),
    }),
    onSubmit: (values) => {
      const { verificationCode, newPassword } = values;

      try {
        resetPassword(verificationCode, newPassword);
      } catch (error: unknown) {
        console.error("Reset password error:", error);

        let errorMessage = "Failed to set new password"
        if (error instanceof Error) {
          errorMessage = error.message
        }

        setSubmissionError(errorMessage);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>
        {/* Instructions */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" align="center">
            Please enter the details below to reset your password.
          </Typography>
        </Grid>

        {/* Verification Code Field */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            id="verificationCode"
            name="verificationCode"
            label="Verification Code"
            variant="outlined"
            value={formik.values.verificationCode}
            onChange={formik.handleChange}
            error={formik.touched.verificationCode && Boolean(formik.errors.verificationCode)}
            helperText={formik.touched.verificationCode && formik.errors.verificationCode}
          />
        </Grid>

        {/* New Password Field */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            id="newPassword"
            name="newPassword"
            label="New Password"
            type="password"
            variant="outlined"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
            helperText={formik.touched.newPassword && formik.errors.newPassword}
          />
        </Grid>

        {/* Confirm Password Field */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            id="confirmPassword"
            name="confirmPassword"
            label="Confirm New Password"
            type="password"
            variant="outlined"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            error={
              formik.touched.confirmPassword &&
              Boolean(formik.errors.confirmPassword)
            }
            helperText={
              formik.touched.confirmPassword && formik.errors.confirmPassword
            }
          />
        </Grid>

        {/* Submit Button */}
        <Grid size={{ xs: 12 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{
              borderRadius: 20,
              fontWeight: 700,
              boxShadow: "0 0 18px rgba(255, 255, 255, 0.0)",
              "&:hover": {
                boxShadow: "0 0 22px rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            Reset Password
          </Button>
        </Grid>

        {/* Error Display */}
        {submissionError && (
          <Grid size={{ xs: 12 }}>
            <Alert variant="outlined" severity="error">
              {submissionError}
            </Alert>
          </Grid>
        )}
      </Grid>
    </form>
  );
};

export default ResetPasswordForm;
